import * as store from '@attain-apps/fe/web/main/lib/store'
import { TypeChecker } from '@attain-apps/type-guards'
import { format } from 'pretty-format'
import * as rxjs from 'rxjs'

import type { Collection } from '../../../../collection'
import * as ecModels from '../../../../models'
import type { Plugin } from '../plugin'

// TODO: when request contains ids, we should get entities in the cache that
// match the ids, and then only request from the backend entities that we don't
// already have in cache. EXCEPT: similar to get, we need to figure out how to
// handle when we are retrieving things, even if they are already in the cache

/**
 * Retrieves a list of entities
 */
export const executeList = store.effects.create(
  <TTypeParams extends ecModels.TypeParams.Constraint>(
    feature: Plugin<TTypeParams>['feature'],
    collection: Collection<TTypeParams>,
  ) => {
    const canList = collection.plugins.apiExecutionPlugin.canExecute('list')
    const canBatchGet =
      collection.plugins.apiExecutionPlugin.canExecute('batchGet')
    const canGet = collection.plugins.apiExecutionPlugin.canExecute('get')
    if (!canList && !canBatchGet && !canGet) {
      // TODO: create effect to automatically dispatch cancel on request
      return undefined
    }
    return feature.actions.list.relay((forwardTo, request) =>
      collection.feature.selectors.context.asObservable.pipe(
        // We need to get the context, but just the most recent value
        rxjs.take(1),
        rxjs.exhaustMap(context => {
          if (request.ids) {
            if (request.ids.length === 0) {
              // When requesting an empty list of IDs, the result is always an
              // empty list of entities
              return rxjs.of({
                success: {
                  response: { entities: [] },
                },
              })
            }
            // TODO: - Caching logic
            // - false - skip the caching logic
            // - true or func => go through each of requested ids and try to get the entities from the cache
            if (request.ids.length === 1 && canGet) {
              // If we are just requesting for one item, and we can execute
              // `get`, then we just use that
              return rxjs.of(
                forwardTo(
                  collection.plugins.apiExecutionPlugin.feature.actions.execute,
                  {
                    input: {
                      args: request.ids,
                      context,
                      methodName: 'get',
                    },
                    output: ({ response }) => ({
                      entities: response
                        ? [
                            // NOTE: since `execute` can return a variety of
                            // results, we need to tell TypeScript which result we
                            // expect
                            response as NonNullable<
                              rxjs.ObservedValueOf<
                                ecModels.Api.Method.Result<
                                  'get',
                                  typeof collection.api
                                >
                              >
                            >,
                          ]
                        : [],
                    }),
                  },
                ),
              )
            } else if (canBatchGet) {
              // If we are requesting more than one item, or cannot execute
              // `get`, we use `batchGet`
              return rxjs.of(
                forwardTo(
                  collection.plugins.apiExecutionPlugin.feature.actions.execute,
                  {
                    input: {
                      args: [request.ids],
                      context,
                      methodName: 'batchGet',
                    },
                    output: ({ response }) => ({
                      // NOTE: since `execute` can return a variety of
                      // results, we need to tell TypeScript which result we
                      // expect
                      entities: response as rxjs.ObservedValueOf<
                        ecModels.Api.Method.Result<
                          'batchGet',
                          typeof collection.api
                        >
                      >,
                    }),
                  },
                ),
              )
            } else if (canGet) {
              // If we cannot execute `batchGet`, we execute a sequence of `get`
              // requests

              /**
               * Accumulated results, kept in the same order as the requested
               * IDs
               *
               * Since there may be a lot, we want to avoid expensive operations
               * like sorting or copying arrays
               */
              const entities = Array<TTypeParams['entity'] | undefined>(
                request.ids.length,
              )
              return rxjs.from(request.ids).pipe(
                rxjs.map((id, i) =>
                  forwardTo(
                    collection.plugins.apiExecutionPlugin.feature.actions
                      .execute,
                    {
                      input: {
                        args: [id],
                        context,
                        methodName: 'get',
                      },
                      output: (
                        { response },
                        // This is created the first time, and reused every
                        // subsequent completion
                        accumulatedResponse = {
                          get entities() {
                            // Once the entities are actually requested, then we
                            // filter out all `undefined` entries (i.e. IDs that
                            // did not successfully retrieve an entity)
                            return entities.filter(TypeChecker.isNotNullish.raw)
                          },
                        },
                      ) => {
                        // NOTE: since `execute` can return a variety of
                        // results, we need to tell TypeScript which result we
                        // expect
                        entities[i] = response as rxjs.ObservedValueOf<
                          ecModels.Api.Method.Result<
                            'get',
                            typeof collection.api
                          >
                        >
                        // Reuses the response from above, which has a getter to
                        // get the live results
                        return accumulatedResponse
                      },
                    },
                  ),
                ),
              )
            }
            // If we cannot execute `batchGet` or `get`, then we must cancel the
            // request
            return rxjs.of({
              cancellation: {
                reason: `Neither 'batchGet' nor 'get' are implemented on the API`,
              },
            })
          }

          // TODO: figure out what has changed in the request (requires storing
          // previous list request(s) and result(s) in the state)

          // TODO: if something has changed that is supported by list, we must
          // execute. Otherwise, we do not need to

          // TODO: if we don't execute, we need to immediately return a locally
          // evaluated list

          // Local filter and pagination do not need attribute metadata. Local
          // sort does, because sorting reads the attribute's `getValueFrom`
          // accessor below. Adding a fixed delay here is wrong: it slows every
          // list request and still does not guarantee that attributes have
          // loaded. Wait only when local sort actually needs attributes.
          const attributes$ = _needsAttributesForLocalSort(collection, request)
            ? _selectAttributesForLocalSort(collection)
            : rxjs.of([])
          return attributes$.pipe(
            rxjs.map(attributes =>
              forwardTo(
                collection.plugins.apiExecutionPlugin.feature.actions.execute,
                {
                  input: {
                    args: [request],
                    context,
                    methodName: 'list',
                  },
                  output: ({ response }) => ({
                    entities: _applyLocalRequestLogic(
                      collection,
                      request,
                      attributes,
                      // NOTE: since `execute` can return a variety of
                      // results, we need to tell TypeScript which result we
                      // expect
                      response as rxjs.ObservedValueOf<
                        ecModels.Api.Method.Result<
                          'list',
                          typeof collection.api
                        >
                      >,
                    ),
                  }),
                },
              ),
            ),
          )
        }),
      ),
    ).asObservable
  },
)

/**
 * Selects attributes once they are usable for local sorting.
 *
 * The attribute selector exposes `[]` before the attributes request completes,
 * which is fine for display code but too early for local sorting. If we sorted
 * with that placeholder value, `_applyLocalRequestLogic` would fail to find the
 * requested sort attribute and leave the list unsorted.
 *
 * Waiting on the raw attribute-plugin state lets us distinguish "not loaded
 * yet" (`attributes === undefined`) from "loaded and empty" (`attributes` is
 * `[]`). Collections without a `getAttributes` API keep the previous
 * non-blocking behavior and fall back to whatever attributes have already been
 * inferred.
 */
function _selectAttributesForLocalSort<
  TTypeParams extends ecModels.TypeParams.Constraint,
>(
  collection: Collection<TTypeParams>,
): rxjs.Observable<readonly ecModels.Attribute<TTypeParams>[]> {
  let state$ = collection.plugins.attributePlugin.feature.state.asObservable
  if (collection.plugins.apiExecutionPlugin.canExecute('getAttributes')) {
    state$ = state$.pipe(
      rxjs.filter(
        ({ attributes, error }) =>
          attributes !== undefined || error !== undefined,
      ),
    )
  }
  return state$.pipe(
    rxjs.take(1),
    rxjs.map(({ attributes }) => attributes ?? []),
  )
}

/**
 * Determines if local request handling needs attribute metadata.
 *
 * Attribute metadata is only required when sorting will happen locally. If the
 * backend list method already applies `sort`, or the request does not include a
 * sort, list execution can proceed immediately without waiting for attributes.
 */
function _needsAttributesForLocalSort<
  TTypeParams extends ecModels.TypeParams.Constraint,
>(
  collection: Collection<TTypeParams>,
  request: Readonly<ecModels.QueryRequest<TTypeParams>>,
): boolean {
  if (!collection.api.list) {
    return false
  }
  const alreadyApplied = ecModels.Api.list.QueryRequestUsage.from(
    collection.api.list,
  )
  return !!request.sort && !alreadyApplied.sort
}

/**
 * Applies whichever parts of the {@link ecModels.QueryRequest} were not already
 * applied by {@link ecModels.Api.list}
 */
function _applyLocalRequestLogic<
  TTypeParams extends ecModels.TypeParams.Constraint,
>(
  collection: Collection<TTypeParams>,
  { filter, pagination, sort }: Readonly<ecModels.QueryRequest<TTypeParams>>,
  attributes: readonly ecModels.Attribute<TTypeParams>[],
  list: readonly TTypeParams['entity'][],
): readonly TTypeParams['entity'][] {
  if (!collection.api.list) {
    // If there is no API method, we won't be able to determine its usage, so we
    // won't know what to apply. Therefore, we just return the list as is
    return list
  }
  const alreadyApplied = ecModels.Api.list.QueryRequestUsage.from(
    collection.api.list,
  )
  if (filter && !alreadyApplied.filter) {
    // Filters the list locally, if needed
    const { searchTerm } = filter
    if (searchTerm) {
      list = list.filter(entity =>
        format(entity, { min: true }).includes(searchTerm),
      )
    }
  }
  if (sort && !alreadyApplied.sort) {
    // Sorts the list locally, if needed
    const { by, descending } =
      ecModels.QueryRequest.Sort.Helpers.WithDefaults.from(sort)
    const sortAttribute = attributes.find(attribute => attribute.id === by)
    if (!sortAttribute) {
      // If we don't have the sort attribute, we can't use it
      console.warn(
        `Failed to sort the ${collection.name} list locally by '${by}'. Could not find the '${by}' attribute.`,
      )
    } else {
      // These are used in the sort function below
      const aIsLess = descending ? 1 : -1
      const aIsMore = -aIsLess
      // Creates a copy that can be sorted
      list = [...list].sort((a, b) => {
        // NOTE: we use `any` since the comparators below work for any data type
        // (but TypeScript throws errors when using them on `unknown`)
        const aSortVal: any = sortAttribute.getValueFrom(a)
        const bSortVal: any = sortAttribute.getValueFrom(b)

        // For string values, perform case-insensitive comparison
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        const aCompare: any =
          typeof aSortVal === 'string' ? aSortVal.toLowerCase() : aSortVal
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        const bCompare: any =
          typeof bSortVal === 'string' ? bSortVal.toLowerCase() : bSortVal

        if (aCompare < bCompare) {
          return aIsLess
        }
        if (aCompare > bCompare) {
          return aIsMore
        }
        return 0
      })
    }
  }
  if (pagination && !alreadyApplied.pagination) {
    // Paginates the list locally, if needed
    const { quantity, skip } =
      ecModels.QueryRequest.Pagination.Helpers.WithDefaults.from(pagination)
    list = list.slice(
      skip,
      quantity === undefined ? undefined : skip + quantity,
    )
  }
  return list
}
